import React, { useState, useRef, useEffect } from 'react';
import apiService from '../services/api';

const IntegratedTerminal = ({ currentProject, isVisible, onToggle }) => {
  const [output, setOutput] = useState([]);
  const [command, setCommand] = useState('');
  const [isExecuting, setIsExecuting] = useState(false);
  const [commandHistory, setCommandHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const outputRef = useRef(null);
  const inputRef = useRef(null);

  const quickCommands = [
    { label: 'npm install', cmd: 'npm install', icon: '📦' },
    { label: 'npm start', cmd: 'npm start', icon: '▶️' },
    { label: 'npm test', cmd: 'npm test', icon: '🧪' },
    { label: 'npm run build', cmd: 'npm run build', icon: '🔨' },
    { label: 'git status', cmd: 'git status', icon: '🔍' },
    { label: 'pip install -r requirements.txt', cmd: 'pip install -r requirements.txt', icon: '🐍' },
    { label: 'python -m pytest', cmd: 'python -m pytest', icon: '🧪' },
    { label: 'ls -la', cmd: 'ls -la', icon: '📂' }
  ];

  useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [output]);

  useEffect(() => {
    if (isVisible && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isVisible]);

  const executeCommand = async (cmd) => {
    if (!cmd.trim() || !currentProject) return;

    setIsExecuting(true);

    // Add command to output
    setOutput(prev => [
      ...prev,
      { type: 'command', text: `$ ${cmd}`, timestamp: new Date() }
    ]);

    // Add to history
    setCommandHistory(prev => [...prev, cmd]);
    setHistoryIndex(-1);

    try {
      const result = await apiService.executeBuild({
        project_id: currentProject.id,
        command: cmd,
        working_directory: '.',
        timeout: 300 // 5 minutes
      });

      // Add output
      if (result.output) {
        setOutput(prev => [
          ...prev,
          { type: 'output', text: result.output, timestamp: new Date() }
        ]);
      }

      // Add errors if any
      if (result.errors) {
        setOutput(prev => [
          ...prev,
          { type: 'error', text: result.errors, timestamp: new Date() }
        ]);
      }

      // Add completion message
      const statusMsg = result.success
        ? `✓ Completed successfully (${result.execution_time.toFixed(2)}s)`
        : `✗ Failed with exit code ${result.exit_code} (${result.execution_time.toFixed(2)}s)`;

      setOutput(prev => [
        ...prev,
        {
          type: result.success ? 'success' : 'error',
          text: statusMsg,
          timestamp: new Date()
        }
      ]);

    } catch (error) {
      setOutput(prev => [
        ...prev,
        { type: 'error', text: `Error: ${error.message}`, timestamp: new Date() }
      ]);
    } finally {
      setIsExecuting(false);
      setCommand('');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    executeCommand(command);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (commandHistory.length > 0) {
        const newIndex = historyIndex === -1
          ? commandHistory.length - 1
          : Math.max(0, historyIndex - 1);
        setHistoryIndex(newIndex);
        setCommand(commandHistory[newIndex]);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (historyIndex > 0) {
        const newIndex = historyIndex - 1;
        setHistoryIndex(newIndex);
        setCommand(commandHistory[newIndex]);
      } else {
        setHistoryIndex(-1);
        setCommand('');
      }
    }
  };

  const clearTerminal = () => {
    setOutput([]);
  };

  if (!isVisible) return null;

  return (
    <div className="h-80 bg-gray-950 border-t border-gray-700 flex flex-col">
      {/* Header */}
      <div className="h-10 bg-gray-900 border-b border-gray-700 flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <span className="text-sm font-medium text-gray-300">Terminal</span>
          {currentProject && (
            <span className="text-xs text-gray-500">({currentProject.name})</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={clearTerminal}
            className="text-xs text-gray-400 hover:text-white px-2 py-1 hover:bg-gray-800 rounded"
          >
            Clear
          </button>
          <button
            onClick={onToggle}
            className="text-gray-400 hover:text-white p-1 hover:bg-gray-800 rounded"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Quick Commands */}
      <div className="bg-gray-900 px-4 py-2 border-b border-gray-800 overflow-x-auto">
        <div className="flex gap-2">
          {quickCommands.map((qc, idx) => (
            <button
              key={idx}
              onClick={() => executeCommand(qc.cmd)}
              disabled={isExecuting || !currentProject}
              className="flex items-center gap-1 px-3 py-1 bg-gray-800 hover:bg-gray-700 disabled:bg-gray-850 disabled:text-gray-600 text-gray-300 rounded text-xs whitespace-nowrap transition-colors"
            >
              <span>{qc.icon}</span>
              <span>{qc.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Output */}
      <div ref={outputRef} className="flex-1 overflow-auto p-4 font-mono text-sm">
        {output.length === 0 ? (
          <div className="text-gray-600 text-xs">
            <p>Welcome to Code Wizard Terminal</p>
            <p className="mt-2">Type a command or click a quick command above to get started.</p>
            <p className="mt-1">Press ↑/↓ to navigate command history</p>
          </div>
        ) : (
          output.map((line, idx) => (
            <div
              key={idx}
              className={`mb-1 ${
                line.type === 'command' ? 'text-blue-400 font-semibold' :
                line.type === 'error' ? 'text-red-400' :
                line.type === 'success' ? 'text-green-400' :
                'text-gray-300'
              }`}
            >
              {line.text.split('\n').map((textLine, lineIdx) => (
                <div key={lineIdx}>{textLine || '\u00A0'}</div>
              ))}
            </div>
          ))
        )}
        {isExecuting && (
          <div className="text-yellow-400 animate-pulse">Executing...</div>
        )}
      </div>

      {/* Input */}
      <form onSubmit={handleSubmit} className="border-t border-gray-800 p-2">
        <div className="flex items-center gap-2 bg-gray-900 rounded px-3 py-2">
          <span className="text-green-400 font-mono text-sm">$</span>
          <input
            ref={inputRef}
            type="text"
            value={command}
            onChange={(e) => setCommand(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isExecuting || !currentProject}
            placeholder={currentProject ? "Enter command..." : "Select a project first"}
            className="flex-1 bg-transparent border-none outline-none text-gray-300 font-mono text-sm placeholder-gray-600"
          />
          {isExecuting && (
            <div className="flex items-center gap-1 text-xs text-yellow-400">
              <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></div>
              Running
            </div>
          )}
        </div>
      </form>
    </div>
  );
};

export default IntegratedTerminal;
